import React, { useState, useEffect } from 'react';
import './ResumeModal.css';
import { 
    FaTimes, 
    FaPrint, 
    FaCopy, 
    FaCheck 
} from 'react-icons/fa';
import { personalInfo } from '../data/personalInfo';
import { experienceData } from '../data/experienceData';
import { educationData, certificationsData, competitiveProfiles } from '../data/qualificationsData';
import { trackEvent } from '../utils/analytics';

const skillCategories = [
    ['PRIORITY #1', 'Backend & Distributed Systems', ['Node.js', 'Express.js', 'Event-Driven Architecture (EDA)', 'Kafka', 'Socket.IO / WebSockets', 'PostgreSQL', 'MongoDB Atlas', 'Redis Cache', 'RESTful APIs', 'Python']],
    ['PRIORITY #2', 'Full-Stack & Mobile Architecture', ['Next.js (App Router / RSC)', 'React 19', 'React Native (iOS/Android)', 'TypeScript', 'BFF Architecture', 'Tailwind CSS', 'Core Web Vitals', 'State (Zustand/Redux)']],
    ['CORE CLOUD', 'Cloud & DevOps Infrastructure', ['AWS (EC2, S3, CloudFront, Lambda)', 'Docker Containers', 'GitHub Actions CI/CD', 'Nginx Reverse Proxy', 'PM2 Process Clusters', 'Linux Server Admin']],
    ['EMERGING AI', 'AI Systems & Intelligent Data Rails', ['Streaming LLM APIs (SSE)', 'LLM Gateways (OpenAI/Replicate)', 'pgvector Vector Indexes', 'Redis Vector Caching', 'Google BigQuery', 'Python Simulations']]
];

const resumeTrackMeta = {
    backend: {
        roleTitle: 'Senior Backend Systems Architect / Founding Lead Engineer',
        scopeSubtitle: 'Node.js | Kafka Event Streaming | Multi-DB | AWS Microservices | AI Data Rails',
        profileSummary: 'Senior Backend Systems Architect & Founding Lead Engineer with 4+ years architecting high-throughput microservices, sub-50ms event streaming, and resilient cloud systems.',
        pillars: [
            ['Distributed Systems & Polyglot Storage', 'Engineered 260+ production microservices using Event-Driven Architecture (EDA), sharded MySQL & DynamoDB partitions, and distributed Redis caching; 99.98% uptime.'],
            ['High-Concurrency & Fault Tolerance', 'Spearheaded clustered Node.js backend & idempotent QR verification engine for AMRITPEX 2023; handled 10,000+ daily attendees with zero downtime.'],
            ['Event Streaming & Cloud Automation', 'Orchestrated sub-200ms Kafka and Socket.IO real-time streaming pipelines; built automated S3/Lambda media queues cutting bandwidth by 70%.']
        ]
    },
    fullstack: {
        roleTitle: 'Lead Full-Stack Software Engineer & Cloud Architect',
        scopeSubtitle: 'Next.js App Router (SSR/RSC) | React & React Native | Node.js | AWS Cloud | BFF Patterns',
        profileSummary: 'Lead Full-Stack Software Engineer & Founding Architect with 4+ years directing technical strategy across high-performance Next.js web applications, mobile platforms, and distributed cloud microservices.',
        pillars: [
            ['Next.js & Modern Frontend Systems', 'Architected production Next.js App Router platforms with React Server Components (RSC), eliminating client waterfalls and achieving 95+ Core Web Vitals.'],
            ['BFF Orchestration & Microservices', 'Engineered 260+ REST APIs, optimized 100+ database schemas, and built Next.js BFF data orchestration layers cutting API latency by 20%.'],
            ['Cloud Infrastructure & Mobile Platforms', 'Deployed Dockerized microservices on AWS (EC2, Lambda, S3, CloudFront), shipped React Native iOS/Android apps, and integrated secure payment rails.']
        ]
    },
    aisystems: {
        roleTitle: 'Senior Backend Systems Architect — AI Platform & Concurrency',
        scopeSubtitle: 'AI Infrastructure & High-Speed Data Rails • Node.js | Python | Streaming SSE | Vector DBs',
        profileSummary: 'Senior Backend Systems Architect specializing in AI-ready backend infrastructure, low-latency inference streaming, and real-time analytics engines.',
        pillars: [
            ['AI Gateways & Inference Streaming', 'Engineered real-time generative AI pipeline gateways (OpenAI & Replicate) with Server-Sent Events (SSE), boosting engagement by 25%.'],
            ['Telemetry & Behavioral Data Warehousing', 'Extracted product insights across 1M+ user events via Google BigQuery on GCP, coupled with Python algorithmic simulation models.'],
            ['Low-Latency Event Rails & Vector Caching', 'Architected Kafka event brokers and Socket.IO backplanes with sub-200ms latency, coupled with Redis and pgvector indexing.']
        ]
    }
};

const trackOrder = {
    backend: [0, 1, 2, 3],
    fullstack: [1, 0, 2, 3],
    aisystems: [3, 0, 2, 1]
};

const getSortedSkillCategories = (track) => 
    (trackOrder[track] || trackOrder.backend).map((idx, i) => {
        const [priority, title, skills] = skillCategories[idx];
        return {
            title,
            skills,
            priority: i === 0 ? 'PRIMARY FOCUS' : priority,
            isFeatured: i === 0
        };
    });

const getProfessionalPrintTitle = (track) => {
    switch (track) {
        case 'fullstack':
            return 'Amit_Kumar_Resume_Lead_FullStack_Architect';
        case 'aisystems':
            return 'Amit_Kumar_Resume_AI_Systems_Architect';
        case 'backend':
        default:
            return 'Amit_Kumar_Resume_Senior_Backend_Architect';
    }
};

const getTrackAchievements = (exp, currentTrack) => {
    if (exp.trackAchievements && exp.trackAchievements[currentTrack]) {
        return exp.trackAchievements[currentTrack];
    }
    return exp.achievements || [];
};

const ResumeModal = ({ isOpen, onClose }) => {
    const [copied, setCopied] = useState(false);
    const [activeSection, setActiveSection] = useState('overview');
    const [track, setTrack] = useState('backend'); // 'backend' (default), 'fullstack', 'aisystems'

    useEffect(() => {
        if (!isOpen) return;
        const originalTitle = document.title;
        const printTitle = getProfessionalPrintTitle(track);

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };

        const handleBeforePrint = () => {
            document.title = printTitle;
        };

        const handleAfterPrint = () => {
            document.title = originalTitle;
        };

        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('beforeprint', handleBeforePrint);
        window.addEventListener('afterprint', handleAfterPrint);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('beforeprint', handleBeforePrint);
            window.removeEventListener('afterprint', handleAfterPrint);
            document.body.style.overflow = '';
            document.title = originalTitle;
        };
    }, [isOpen, onClose, track]);

    if (!isOpen) return null;

    const handlePrint = () => {
        trackEvent('resume_pdf_download', { track });
        const originalTitle = document.title;
        document.title = getProfessionalPrintTitle(track);
        window.print();
        setTimeout(() => {
            document.title = originalTitle;
        }, 1000);
    };

    const scrollToSection = (sectionKey) => {
        trackEvent('resume_section_nav', { section: sectionKey, track });
        setActiveSection(sectionKey);
        const sectionEl = document.getElementById(`sec-${sectionKey}`);
        if (sectionEl && typeof sectionEl.scrollIntoView === 'function') {
            sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleCopyText = () => {
        trackEvent('resume_ats_copy', { track });
        const meta = resumeTrackMeta[track];
        const plainText = `AMIT KUMAR
${meta.roleTitle}
Email: ${personalInfo.contact.email} | Phone: ${personalInfo.contact.phone} | Location: ${personalInfo.contact.location}
Web: https://devamit.in | GitHub: github.com/devamitin | LinkedIn: linkedin.com/in/devamitin

SUMMARY & ARCHITECTURAL THESIS
${meta.profileSummary}
Core Pillars:
${meta.pillars.map(([title, desc]) => `• ${title}: ${desc}`).join('\n')}

TECHNICAL ARSENAL & SKILLS MATRIX
${skillCategories.map(([priority, category, skills]) => `[${priority}] ${category}:\n${skills.join(', ')}`).join('\n\n')}

VERIFIED PRODUCTION EXPERIENCE
${experienceData.map(exp => `• ${exp.role} | ${exp.company} (${exp.period})
  Scale/Impact: ${exp.scale}
  Core System: ${exp.description}
  Key Architectural Deliverables:
${getTrackAchievements(exp, track).map(a => `    - ${a}`).join('\n')}`).join('\n\n')}

EDUCATION & RIGOR
• ${educationData.degree} | ${educationData.period}
  ${educationData.institution} • MAKAUT Affiliated • AICTE Approved
  Disciplines: ${educationData.coreDisciplines.join(', ')}
• Algorithmic Problem Solving: ${competitiveProfiles.map(cp => `${cp.name} (${cp.tag}, ${cp.stat})`).join(' • ')}`;

        navigator.clipboard.writeText(plainText.trim()).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="resume-backdrop" onClick={onClose}>
            <div className="resume-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Executive CV">
                {/* Modal Top Control Bar - Portfolio Cyber Slate & Cyan Palette */}
                <div className="resume-header">
                    <div className="resume-header-title">
                        <span className="spec-badge">VERIFIED SPEC SHEET</span>
                        <h3>Executive Curriculum Vitae</h3>
                    </div>

                    <div className="resume-actions">
                        <button 
                            className={`resume-copy-btn ${copied ? 'copied' : ''}`} 
                            onClick={(e) => {
                                e.currentTarget.blur();
                                handleCopyText();
                            }} 
                            aria-label="Copy ATS plain text resume"
                            title={`Copy ATS text (${track})`}
                        >
                            {copied ? <FaCheck /> : <FaCopy />}
                            <span>{copied ? 'Copied ATS Text' : `Copy ATS (${track === 'backend' ? 'Backend' : track === 'fullstack' ? 'Full-Stack' : 'AI'})`}</span>
                        </button>

                        <button 
                            className="resume-print-btn" 
                            onClick={(e) => {
                                e.currentTarget.blur();
                                handlePrint();
                            }} 
                            aria-label="Print or Save CV as PDF"
                        >
                            <FaPrint /> <span>Print / Save PDF</span>
                        </button>

                        <span className="resume-header-divider" aria-hidden="true" />

                        <button className="resume-close-btn" onClick={onClose} aria-label="Close CV Modal">
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {/* Sub Command & Quick Section Navigation Bar - Portfolio Palette */}
                <div className="resume-nav-bar">
                    <div className="resume-nav-tabs">
                        {[
                            ['overview', 'Overview'],
                            ['skills', 'Technical Arsenal'],
                            ['experience', 'Experience'],
                            ['credentials', 'Accreditations & Rigor']
                        ].map(([secKey, secLabel]) => (
                            <button 
                                key={secKey}
                                className={`resume-nav-tab ${activeSection === secKey ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.currentTarget.blur();
                                    scrollToSection(secKey);
                                }}
                            >
                                {secLabel}
                            </button>
                        ))}
                    </div>

                    {/* Role Target Focus Selector */}
                    <div className="resume-variant-picker" role="group" aria-label="Resume Target Focus Track">
                        <span className="variant-picker-label">Target:</span>
                        {[
                            ['backend', 'Backend'],
                            ['fullstack', 'Full-Stack'],
                            ['aisystems', 'AI Systems']
                        ].map(([k, label]) => (
                            <button 
                                key={k}
                                className={`variant-pill ${track === k ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.currentTarget.blur();
                                    trackEvent('resume_track_switch', { track: k });
                                    setTrack(k);
                                }}
                                title={`Focus: ${label}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Document Scroll Viewport */}
                <div className="resume-body-viewport">
                    {/* Printable Standard Document Sheet (ATS-Compliant) */}
                    <div className="resume-sheet">
                    {/* Header: Linear Standard Masthead (Single-Column, ATS-Compliant) */}
                    <div className="sheet-top ats-masthead" id="sec-overview">
                        <h1 className="sheet-name ats-name">AMIT KUMAR</h1>
                        <div className="ats-primary-role">
                            {resumeTrackMeta[track].roleTitle}
                        </div>
                        <div className="ats-contact-bar">
                            <div className="ats-contact-row">
                                <span className="ats-c-item">New Delhi, India (IST)</span>
                                <span className="ats-c-sep">•</span>
                                <span className="ats-c-item">
                                    <a href={`tel:${personalInfo.contact.phone.replace(/\s+/g, '')}`} className="ats-c-link">
                                        {personalInfo.contact.phone}
                                    </a>
                                </span>
                                <span className="ats-c-sep">•</span>
                                <span className="ats-c-item">
                                    <a href={`mailto:${personalInfo.contact.email}`} className="ats-c-link">
                                        {personalInfo.contact.email}
                                    </a>
                                </span>
                            </div>
                            <div className="ats-contact-row">
                                <span className="ats-c-item">
                                    <a href="https://devamit.in" target="_blank" rel="noopener noreferrer" className="ats-c-link">
                                        devamit.in
                                    </a>
                                </span>
                                <span className="ats-c-sep">•</span>
                                <span className="ats-c-item">
                                    <a href={personalInfo.socialLinks.github.url} target="_blank" rel="noopener noreferrer" className="ats-c-link">
                                        github.com/devamitin
                                    </a>
                                </span>
                                <span className="ats-c-sep">•</span>
                                <span className="ats-c-item">
                                    <a href={personalInfo.socialLinks.linkedin.url} target="_blank" rel="noopener noreferrer" className="ats-c-link">
                                        linkedin.com/in/devamitin
                                    </a>
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Executive Summary */}
                    <div className="sheet-section ats-section">
                        <h2 className="ats-sec-heading">PROFESSIONAL SUMMARY</h2>
                        <p className="ats-summary-paragraph">
                            {resumeTrackMeta[track].profileSummary}
                        </p>
                        <ul className="ats-summary-list">
                            {resumeTrackMeta[track].pillars.map(([title, desc], pIdx) => (
                                <li key={pIdx}>
                                    <strong>{title}:</strong> {desc}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Technical Skills (Linear Categorized Rows) */}
                    <div className="sheet-section ats-section" id="sec-skills">
                        <h2 className="ats-sec-heading">TECHNICAL SKILLS</h2>
                        <div className="ats-skills-table">
                            {getSortedSkillCategories(track).map((cat) => (
                                <div className="ats-skill-line" key={cat.title}>
                                    <strong className="ats-skill-category-title">{cat.title}:</strong>{' '}
                                    <span className="ats-skill-values">{cat.skills.join(', ')}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Professional Experience (Chronological with Natural Bullets) */}
                    <div className="sheet-section ats-section" id="sec-experience">
                        <h2 className="ats-sec-heading">PROFESSIONAL EXPERIENCE</h2>
                        <div className="ats-experience-list">
                            {experienceData.map((exp) => (
                                <div className="ats-exp-entry" id={`exp-${exp.id}`} key={exp.id}>
                                    <div className="ats-exp-header">
                                        <div className="ats-exp-title-company">
                                            <strong className="ats-job-role">{exp.role}</strong>
                                            <span className="ats-role-company-sep"> — </span>
                                            {exp.url ? (
                                                <a href={exp.url} target="_blank" rel="noopener noreferrer" className="ats-company-link">
                                                    {exp.company}
                                                </a>
                                            ) : (
                                                <span className="ats-company-name">{exp.company}</span>
                                            )}
                                        </div>
                                        <span className="ats-exp-date">{exp.duration}</span>
                                    </div>

                                    {exp.id === 'phnk-networks' && (
                                         <div className="ats-promotion-note">
                                             <em>Promotion Trajectory: Promoted from Software Developer to Founding Lead Architect</em>
                                         </div>
                                    )}

                                    <p className="ats-exp-overview">{exp.summary}</p>

                                    {getTrackAchievements(exp, track).length > 0 && (
                                        <ul className="ats-bullet-list">
                                            {getTrackAchievements(exp, track).map((ach, aIdx) => (
                                                <li key={aIdx}>
                                                    <strong>[{ach.metric}]</strong> {ach.label}
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    {exp.techStack && (
                                        <div className="ats-tech-line">
                                            <strong>Core Technologies:</strong> {exp.techStack.join(', ')}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Education & Academic Rigor */}
                    <div className="sheet-section ats-section" id="sec-education">
                        <h2 className="ats-sec-heading">EDUCATION &amp; RIGOR</h2>
                        <div className="ats-edu-entry">
                            <div className="ats-exp-header">
                                <div className="ats-exp-title-company">
                                    <strong className="ats-degree-title">{educationData.degree}</strong>
                                </div>
                                <span className="ats-exp-date">{educationData.period}</span>
                            </div>
                            <div className="ats-edu-meta-line">
                                <span className="ats-institution">{educationData.institution}</span>
                                <span className="ats-sep">•</span>
                                <span>MAKAUT Affiliated</span>
                                <span className="ats-sep">•</span>
                                <span>AICTE Approved</span>
                            </div>
                            <div className="ats-edu-disciplines">
                                <strong>Relevant Coursework:</strong> {educationData.coreDisciplines.join(', ')}
                            </div>
                        </div>

                        <div className="ats-rigor-block">
                            <strong>Algorithmic Problem Solving:</strong>{' '}
                            {competitiveProfiles.map(cp => `${cp.name} (${cp.tag}, ${cp.stat})`).join(' • ')}
                        </div>
                    </div>

                    {/* Verified Industry Accreditations */}
                    <div className="sheet-section ats-section" id="sec-credentials">
                        <h2 className="ats-sec-heading">LICENSES &amp; CERTIFICATIONS</h2>
                        <ul className="ats-bullet-list">
                            {certificationsData.map((c, idx) => (
                                <li key={idx}>
                                    <strong>{c.title}</strong> — {c.issuer} ({c.period})
                                    <div className="ats-cert-desc">{c.skills}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
    );
};

export default ResumeModal;
